import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import { message } from 'antd';
import classNames from 'classnames';
import { installAndroidSoft, installAndroidSoftList } from '../../actions/anative';
import { logging, logPostInfo } from '../../actions/log';
import {
  getLocalSoft,
  installSoft,
  installSoftList,
  setUpdateNotifyStatus,
} from '../../actions/native';
import CommReco from '../../components/CommReco/CommReco';
import LineCardDReco from '../../components/LineCardDReco/LineCardDReco';
import LineCardUpdate from '../../components/LineCardUpdate/LineCardUpdate';
import Loading from '../../components/Loading/Loading';
import { CONTEXT } from '../../config';
import Banner from '../../static/update_none.png';
import { getQueryString } from '../../utils/utils';
import styles from './Update.module.less';
import { PCStoreJsBridge } from '../../nativeapi';
import fail from '../../static/msg-fail.svg';
import success from '../../static/msg-success.svg';

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectDownloadSofts = createSelector(
  state => state.download,
  download => download.softs,
);

const selectSearchRecommend = createSelector(
  state => state.search,
  search => search.searchRecommend,
);

const selectUpdateNotifyStatus = createSelector(
  state => state.native,
  native => native.updateNotifyStatus,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);
const selectIsEnabledSilent = createSelector(
  state => state.layout,
  layout => layout.isEnabledSilent,
);

export default memo(() => {
  const dispatch = useDispatch();
  const location = useLocation();
  const upgradeSoftsData = useSelector(selectUpgradeSofts);
  const downloadSofts = useSelector(selectDownloadSofts); // 正在下载
  const logInfoData = useSelector(selectLogInfo);
  const instRecommend = useSelector(selectSearchRecommend); // 点击更新后的推荐
  const updateNotifyStatus = useSelector(selectUpdateNotifyStatus); // 升级弹窗开关状态
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const isEnabledSilent = useSelector(selectIsEnabledSilent); // 是否打开快速静默安装

  const [checkedInfo, setCheckedInfo] = useState({});
  const [checkedAll, setCheckedAll] = useState(true);
  const [checkedLength, setCheckedLength] = useState(0);

  /**
   * 统计页面浏览时长
   */
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Update`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
          },
        }),
      );
    };
  }, []);

  const getCheckSoft = useCallback((checked_info, upgrade_softs) => {
    const checkedArr = upgrade_softs.filter(soft => checked_info[soft.softID] === true);
    return checkedArr;
  });

  const handleShowOther = useCallback(() => {
    // 点击随便逛逛
    dispatch(
      logging({
        category: 'C',
        action: 'Update_SeeOther',
        label: '',
        param2: {
          source: 'Update',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
  }, [logInfoData]);

  const handleOneCheckbox = useCallback(
    (softID, checked) => {
      checkedInfo[softID] = checked;
      const checkedArr = getCheckSoft(checkedInfo, upgradeSoftsData);
      if (checkedArr.length === upgradeSoftsData.length) {
        setCheckedAll(true);
      } else if (checkedAll) {
        setCheckedAll(!checkedAll);
      }
      setCheckedInfo(checkedInfo);
      setCheckedLength(checkedArr.length);
    },
    [checkedInfo, upgradeSoftsData],
  );

  const handleAllCheck = useCallback(() => {
    upgradeSoftsData.forEach(soft => {
      checkedInfo[soft.softID] = !checkedAll;
    });
    const checkedArr = getCheckSoft(checkedInfo, upgradeSoftsData);

    setCheckedAll(!checkedAll);
    setCheckedInfo(checkedInfo);
    setCheckedLength(!checkedAll ? checkedArr.length : 0);
  }, [checkedInfo, upgradeSoftsData, checkedAll]);

  /**
   * 点击全部下载
   */
  const handleAllUpdate = useCallback(() => {
    const checkedArr = getCheckSoft(checkedInfo, upgradeSoftsData);
    const softIDs = [];
    checkedArr.forEach(
      softItem => {
        // 将没有在下载任务里面的和下载失败的继续添加下载
        const found =
          downloadSofts &&
          downloadSofts.find(
            soft =>
              soft.status !== 4 &&
              (soft.softID === softItem.softID || soft.softID === softItem.SoftID),
          );
        if (found) {
          return;
        }

        softIDs.push(softItem.softID || softItem.SoftID);

        const newSoftItem = { ...softItem };
        newSoftItem.logInfo = {
          source: 'Update',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          softid: softItem.androidType === '1' ? softItem.packageName : softItem.softID,
          softName: softItem.softName,
          softType: softItem.androidType === '1' ? '1' : '0',
          type: 'u',
          url: softItem.downLoadUrl,
        };

        if (newSoftItem.androidType === '1') {
          dispatch(installAndroidSoft({ ...newSoftItem, isClick: 'click_update_all' }));
        } else {
          let installparams = {};
          if (silentInstSoft && silentInstSoft.length > 0 && isEnabledSilent) {
            const silentSoft = silentInstSoft.find(soft_ => soft_.softID === newSoftItem.softID);
            if (silentSoft && silentSoft.softID) {
              newSoftItem.logInfo.type = 'xu';
              if (silentSoft.silentParamDic) {
                installparams.quiet = silentSoft.silentParamDic;
              }
              if (silentSoft.silentDirParamDic) {
                installparams.todir = silentSoft.silentDirParamDic;
              }
            }
          }
          dispatch(installSoft({ ...newSoftItem, installparams, isClick: 'click_update_all' }));
        }
      },
      [checkedInfo, upgradeSoftsData, silentInstSoft, isEnabledSilent],
    );

    if (softIDs.length > 0) {
      // 更新页面 点击全部升级 打桩
      dispatch(
        logging({
          category: 'C',
          action: 'Upgrade_All',
          label: '',
          param2: {
            source: 'Update',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            softids: softIDs.join(', '), // 选中/取消选中
            ckall: checkedAll ? 0 : 1,
          },
        }),
      );
    }
  }, [dispatch, logInfoData, downloadSofts, checkedAll, checkedInfo, upgradeSoftsData]);

  useEffect(() => {
    if (upgradeSoftsData && upgradeSoftsData.length > 0) {
      const checkedInfoObj = {};
      const softIDs = [];
      upgradeSoftsData.forEach(soft => {
        checkedInfoObj[soft.softID] = true;
        softIDs.push(soft.softID || soft.SoftID);
      });
      setCheckedInfo(checkedInfoObj);
      setCheckedLength(upgradeSoftsData.length);
    }
  }, [upgradeSoftsData]);

  // 更新传入的软件
  useEffect(() => {
    const softIdStr = getQueryString('softIds', location.search) || '';
    const pnsStr = getQueryString('pns', location.search) || '';
    const bizId = getQueryString('bizId', location.search) || '';
    const bizType = getQueryString('bizType', location.search) || '';
    const softIds = softIdStr.split(',');
    const pns = pnsStr.split(',');
    const upgradeIsDownload = sessionStorage.getItem('upgradeIsDownload'); // 拒绝重复下载
    if (
      upgradeSoftsData &&
      upgradeSoftsData.length > 0 &&
      silentInstSoft &&
      silentInstSoft.length >= 0 &&
      (!upgradeIsDownload || upgradeIsDownload !== `${softIdStr}&${pnsStr}`)
    ) {
      let bizInfo = {};
      if (bizId && bizType) {
        bizInfo = { bizId, bizType };
      }
      if ((softIds && softIds.length > 0) || (pns && pns.length > 0)) {
        const androidSoftList = [];
        const pcSoftList = [];
        upgradeSoftsData.forEach(softItem => {
          const logInfo = {
            source: 'Update',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            softid: softItem.androidType === '1' ? softItem.packageName : softItem.softID,
            softName: softItem.softName,
            softType: softItem.androidType === '1' ? '1' : '0',
            type: 'u',
            url: softItem.downLoadUrl,
          };
          if (softItem.androidType !== '1' && softIds && softIds.length > 0) {
            softIds.forEach(softId => {
              if (softItem.softID === softId) {
                let installparams = {};
                if (silentInstSoft && silentInstSoft.length > 0 && isEnabledSilent) {
                  const silentSoft = silentInstSoft.find(soft_ => soft_.softID === softItem.softID);
                  if (silentSoft && silentSoft.softID) {
                    logInfo.type = 'xu';
                    if (silentSoft.silentParamDic) {
                      installparams.quiet = silentSoft.silentParamDic;
                    }
                    if (silentSoft.silentDirParamDic) {
                      installparams.todir = silentSoft.silentDirParamDic;
                    }
                  }
                }
                pcSoftList.push({ ...softItem, logInfo, installparams, bizInfo });
              }
            });
          }
          if (softItem.androidType === '1' && pns && pns.length > 0) {
            pns.forEach(pn => {
              if (softItem.packageName === pn) {
                androidSoftList.push({ ...softItem, logInfo, bizInfo });
              }
            });
          }
        });
        if (androidSoftList.length > 0) {
          dispatch(installAndroidSoftList(androidSoftList));
        }
        if (pcSoftList.length > 0) {
          dispatch(installSoftList(pcSoftList));
        }
        sessionStorage.setItem('upgradeIsDownload', `${softIdStr}&${pnsStr}`);
      }
    }
  }, [dispatch, upgradeSoftsData, logInfoData, location.search]);

  useEffect(() => {
    dispatch(getLocalSoft());
  }, [dispatch]);

  useEffect(() => {
    // 更新页面 PV
    dispatch(
      logging({
        category: 'P',
        action: 'Update',
        label: '',
        param2: {
          source: 'Update',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
    dispatch(
      logPostInfo({
        source: 'Update',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  }, [dispatch]);
  const getUpdateNotifyStatus = () => {
    PCStoreJsBridge.getAppStoreSwitch({
      type: 1,
      //   UNKNOWN = 0,
      //   SWITCH_UPGRADE_POPUP = 1, // 升级弹窗开关
      //   SWITCH_RIGHT_MENU = 2,// 右键菜单开关
    }).then(({ succ, enable }) => {
      if (succ) {
        dispatch(setUpdateNotifyStatus(enable));
      }
    });
  };
  useEffect(getUpdateNotifyStatus, []);
  const handleUpdateNotifyStatus = () => {
    PCStoreJsBridge.setAppStoreSwitch({
      type: 1,
      enable: true,
      source: 1 /* 1：设置中心勾选框创建；2：关闭商店时创建；3：创建商店时创建  */,
    }).then(
      () => {
        getUpdateNotifyStatus();
        message.success({
          content: '设置成功！',
          icon: <img className="ant-message-custom-img" src={success} alt="success" />,
        });
      },
      () => {
        message.error({
          content: '设置失败！',
          icon: <img className="ant-message-custom-img" src={fail} alt="success" />,
        });
      },
    );

    // 打桩
    dispatch(
      logging({
        category: 'C',
        action: 'Upgrade_Open',
        label: '',
        param2: {
          source: 'Update',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        },
      }),
    );
  };

  return (
    <div className={styles.update}>
      {/* loading */}
      {upgradeSoftsData === null && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
      {upgradeSoftsData && upgradeSoftsData.length > 0 && (
        <div className={styles.updating}>
          <div className={styles.updating_header}>
            <div className={styles.header_left}>
              <div className={styles.header_left_label} onClick={handleAllCheck}>
                {checkedAll ? (
                  <div className={styles.nopopChange} />
                ) : (
                  <div className={styles.nopop} />
                )}
                <span>全选</span>
              </div>
              <div className={styles.header_left_info}>
                共有<span className={styles.header_left_info_num}> {upgradeSoftsData.length} </span>
                款软件可以升级
              </div>
            </div>
            <div
              className={checkedLength === 0 ? styles.header_btn_disable : styles.header_btn}
              onClick={() => {
                if (checkedLength !== 0) handleAllUpdate();
              }}
            >
              全部升级
            </div>
          </div>
          <div className={styles.updating_list}>
            {upgradeSoftsData.map((soft, i) =>
              soft.androidType === '1' ? (
                <React.Fragment key={i}>
                  <LineCardUpdate
                    soft={soft}
                    logInfo={{ action: 'Upgrade_Single', source: 'Update' }}
                    checkedInfo={checkedInfo}
                    changeCheckbox={handleOneCheckbox}
                    key={i}
                  />
                  {instRecommend[soft.packageName] &&
                    instRecommend[soft.packageName].apps &&
                    instRecommend[soft.packageName].apps.length > 4 && (
                      <LineCardDReco
                        recommendData={instRecommend[soft.packageName]}
                        info={{ isHiddleName: true }}
                        logInfo={{
                          source: `Upgrade_Single|| ||DReco||${soft.packageName}`,
                          source_p: logInfoData.source_p,
                          source_pp: logInfoData.source_pp,
                        }}
                      />
                    )}
                </React.Fragment>
              ) : (
                <React.Fragment key={i}>
                  <LineCardUpdate
                    soft={soft}
                    logInfo={{ action: 'Upgrade_Single', source: 'Update' }}
                    checkedInfo={checkedInfo}
                    changeCheckbox={handleOneCheckbox}
                  />
                  {instRecommend[soft.softID] &&
                    instRecommend[soft.softID].apps &&
                    instRecommend[soft.softID].apps.length > 4 && (
                      <LineCardDReco
                        recommendData={instRecommend[soft.softID]}
                        info={{ isHiddleName: true }}
                        logInfo={{
                          source: `Upgrade_Single|| ||DReco||${soft.softID}`,
                          source_p: logInfoData.source_p,
                          source_pp: logInfoData.source_pp,
                        }}
                      />
                    )}
                </React.Fragment>
              ),
            )}
          </div>
        </div>
      )}
      {upgradeSoftsData && upgradeSoftsData.length === 0 && (
        <div className={styles.updated}>
          <div className={styles.updated_banner}>
            <img className={styles.updated_banner_img} src={Banner} alt="" />
            <div className={styles.updated_banner_desc}>
              暂无可更新软件
              <Link
                className={styles.updated_banner_desc_link}
                to={{ pathname: `${CONTEXT}/recommend` }}
                onClick={handleShowOther}
              >
                随便逛逛
              </Link>
            </div>
          </div>
        </div>
      )}
      {upgradeSoftsData !== null && !updateNotifyStatus && (
        <div
          className={classNames(
            styles.updateNotify,
            upgradeSoftsData && upgradeSoftsData.length === 0
              ? styles.updateNotify_transup
              : styles.updateNotify_transdown,
          )}
        >
          您尚未开启更新提醒，建议您
          <span onClick={handleUpdateNotifyStatus} className={styles.updateNotify_btn}>
            &nbsp;立即开启&nbsp; &gt;
          </span>
        </div>
      )}
      <CommReco logInfo={{ tag: 'Update' }} />
    </div>
  );
});
